if (!customElements.get('card-gallery')) {
  customElements.define(
    'card-gallery',
    class CardGallery extends HTMLElement {
      connectedCallback() {
        // Una sola vez: collection-grid-layout.js mueve cards dentro/fuera de las filas
        // `row_grid`, y cada movimiento vuelve a disparar connectedCallback (duplicaría los
        // listeners de las flechas → avanzaría dos fotos por clic).
        if (this._initialized) return;
        this._initialized = true;

        this._track = this.querySelector('.card-gallery__track');
        this._prevBtn = this.querySelector('.card-gallery__prev');
        this._nextBtn = this.querySelector('.card-gallery__next');

        if (!this._track) return;

        this._slides = this._track.querySelectorAll('.card-gallery__slide');

        this._count = this._slides.length;
        this._currentIndex = 0;
        this._hydrated = false;
        this._firstSlideHydrated = false;

        // No depende del tope de _count <= 1 de abajo: una card-gallery de 1 sola foto
        // (alt_gallery_count == 0) también puede necesitar hidratar esa foto si ya está
        // visible al cargar (usuario recurrente con vista-2/vista-4 guardada).
        this._hydrateFirstSlideIfVisible();

        if (this._count <= 1 || !this._prevBtn || !this._nextBtn) return;

        this._track.addEventListener('pointerdown', () => this._hydrateSlides(), {
          once: true,
          passive: true,
        });

        this._updateArrowColor();

        this._prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._hydrateSlides();
          const count = this._visibleSlides().length;
          this._goTo(this._currentIndex <= 0 ? count - 1 : this._currentIndex - 1);
        });

        this._nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._hydrateSlides();
          const count = this._visibleSlides().length;
          this._goTo(this._currentIndex >= count - 1 ? 0 : this._currentIndex + 1);
        });

        this._track.addEventListener(
          'scroll',
          () => {
            if (this._scrollTimer) clearTimeout(this._scrollTimer);
            this._scrollTimer = setTimeout(() => {
              const w = this._track.clientWidth;
              if (w > 0) this._currentIndex = Math.round(this._track.scrollLeft / w);
              this._updateArrowColor();
            }, 80);
          },
          { passive: true },
        );
      }

      // Slides que se ven en esta plataforma: la galería alternativa puede llevar slides
      // --mobile-only / --desktop-only (display:none por media query, ver
      // scss/pages/_collection.scss), que no ocupan sitio en el track, así que los índices
      // del scroll y de las flechas se cuentan sin ellos.
      _visibleSlides() {
        return Array.from(this._slides).filter((slide) => slide.offsetParent !== null);
      }

      _goTo(index) {
        this._currentIndex = index;
        this._track.scrollTo({ left: index * this._track.clientWidth, behavior: 'smooth' });
        this._updateArrowColor();
      }

      // Repone src/srcset de una <img> diferida y, si va envuelta en <picture> (solo la
      // 1ª foto de cada galería lleva el <source media="(max-width:999px)"> del cap de
      // mobile), también su(s) <source data-srcset>. Sin esto, hidratar solo el <img> deja
      // el <source> a medias — inválido para el navegador (sin srcset), que cae de vuelta
      // al <img> igualmente en la mayoría de casos, pero pierde el cap de 900px en mobile.
      _hydrateImg(img) {
        const picture = img.closest('picture');
        if (picture) {
          picture.querySelectorAll('source[data-srcset]').forEach((source) => {
            source.srcset = source.dataset.srcset;
            delete source.dataset.srcset;
          });
        }
        img.src = img.dataset.src;
        if (img.dataset.srcset) img.srcset = img.dataset.srcset;
        delete img.dataset.src;
        delete img.dataset.srcset;
      }

      // Las fotos más allá de la 1ª se renderizan con data-src/data-srcset (ver
      // snippets/card.liquid) para que el navegador no las descargue solo por estar
      // geométricamente cerca del viewport (el carrusel las coloca en fila con
      // overflow:hidden, y el loading="lazy" nativo no distingue eso de "está
      // debajo, ya casi visible"). Se rellenan de golpe en el primer gesto real
      // sobre esta card en concreto. pointerdown en el track se dispara con cualquier botón
      // del ratón (incluido el derecho, antes de abrir el menú contextual), así que esto
      // también hidrata la 1ª foto si el usuario nunca llegó a activar la vista alternativa
      // — en ese caso _hydrateFirstSlideIfVisible() ya no tiene nada que hacer (ver su guard
      // de [data-src]), lo cual es correcto.
      _hydrateSlides() {
        if (this._hydrated) return;
        this._hydrated = true;
        this._slides.forEach((slide) => {
          const img = slide.querySelector('img.card__img[data-src]');
          if (!img) return;
          this._hydrateImg(img);
        });
      }

      // La 1ª foto de la copia de galería que empieza oculta (vista alternativa: vista-2
      // mobile / vista-4 desktop) se renderiza con data-src (ver snippets/card.liquid) para
      // no descargarla nunca si el usuario no llega a activar esa vista — casi nadie lo hace,
      // y hasta ahora se cargaba igual que la copia visible, duplicando la memoria de cada
      // card. Se hidrata sola en cuanto esta card-gallery deja de estar oculta (ver el
      // listener de 'collection:viewToggled' más abajo y en connectedCallback para el caso
      // de un usuario recurrente que ya carga con esa vista activa).
      _hydrateFirstSlideIfVisible() {
        if (this._firstSlideHydrated) return;
        if (this.offsetParent === null) return; // sigue oculta (display:none en algún ancestro)

        // 1ª slide VISIBLE, no _slides[0]: la 1ª del marcado puede ser de la otra plataforma.
        const firstSlide = this._slides && this._visibleSlides()[0];
        const img = firstSlide && firstSlide.querySelector('img.card__img[data-src]');
        if (!img) return; // copia "enriquecida" (nunca tiene data-src en su 1ª foto) o ya hidratada

        this._firstSlideHydrated = true;
        this._hydrateImg(img);
      }

      // Samples the currently visible slide's image to decide whether each arrow
      // needs a light or dark tone to stand out — same approach as the PDP header
      // icon color detection (assets/pdp.js), applied per-card instead of per-page.
      _updateArrowColor() {
        const slide = this._visibleSlides()[this._currentIndex];
        const img = slide && slide.querySelector('img.card__img');
        if (!img) return;

        const onCurrentSlideLoaded = () => {
          this._sampleBehindButton(img, this._prevBtn);
          this._prefetchNeighborSlides();
        };

        if (img.complete && img.naturalWidth) {
          onCurrentSlideLoaded();
        } else {
          img.addEventListener('load', onCurrentSlideLoaded, { once: true });
        }
      }

      // El primer deslizamiento mostraba un marco en blanco: _hydrateSlides() solo arranca
      // en pointerdown, y la descarga no siempre termina antes de que acabe la animación
      // del swipe. Aquí se adelanta la vecina inmediata (solo 1 foto, no la galería entera)
      // en cuanto la que se está viendo ahora mismo YA ha cargado — usa su loading="lazy"
      // nativo (o, en la galería alternativa, el momento en que se activa esa vista) como
      // señal de "esta card ya importa", así no se descarga nada de cards lejos del
      // viewport. Se llama en cada _updateArrowColor(), pero _hydrateImg() ya borra
      // data-src al hidratar, así que repetir la llamada en vecinas ya cargadas no vuelve
      // a pedir nada.
      _prefetchNeighborSlides() {
        const slides = this._visibleSlides();
        [this._currentIndex - 1, this._currentIndex + 1].forEach((i) => {
          const slide = slides[i];
          const img = slide && slide.querySelector('img.card__img[data-src]');
          if (img) this._hydrateImg(img);
        });
      }

      // Maps the left arrow's actual rendered rect onto the image's natural pixel
      // space (accounting for the object-fit: cover scale/crop) so we sample
      // exactly what's visually behind it. Both arrows share this result so they
      // never show up in two different colors on the same card.
      _sampleBehindButton(img, btn) {
        try {
          const imgRect = img.getBoundingClientRect();
          const btnRect = btn.getBoundingClientRect();
          if (!imgRect.width || !imgRect.height || !btnRect.width || !btnRect.height) return;

          const scale = Math.max(imgRect.width / img.naturalWidth, imgRect.height / img.naturalHeight);
          const cropX = (img.naturalWidth * scale - imgRect.width) / 2;
          const cropY = (img.naturalHeight * scale - imgRect.height) / 2;

          let sx = (btnRect.left - imgRect.left + cropX) / scale;
          let sy = (btnRect.top - imgRect.top + cropY) / scale;
          let regionW = btnRect.width / scale;
          let regionH = btnRect.height / scale;

          sx = Math.max(0, Math.min(sx, img.naturalWidth - 1));
          sy = Math.max(0, Math.min(sy, img.naturalHeight - 1));
          regionW = Math.max(1, Math.min(regionW, img.naturalWidth - sx));
          regionH = Math.max(1, Math.min(regionH, img.naturalHeight - sy));

          const sampleW = 20;
          const sampleH = 20;
          const canvas = document.createElement('canvas');
          canvas.width = sampleW;
          canvas.height = sampleH;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, sx, sy, regionW, regionH, 0, 0, sampleW, sampleH);
          const data = ctx.getImageData(0, 0, sampleW, sampleH).data;

          let luminance = 0;
          for (let i = 0; i < data.length; i += 4) {
            luminance += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          }
          luminance /= data.length / 4;

          const isDark = luminance <= 120;
          this._prevBtn.classList.toggle('is-bg-dark', isDark);
          this._prevBtn.classList.toggle('is-bg-light', !isDark);
          this._nextBtn.classList.toggle('is-bg-dark', isDark);
          this._nextBtn.classList.toggle('is-bg-light', !isDark);
        } catch {
          // CORS blocked or other error — leave arrow color unchanged
        }
      }
    },
  );

  // La galería alternativa (vista-2 mobile / vista-4 desktop) va envuelta en <template>
  // en snippets/card.liquid — su contenido es inerte (sin layout, sin subir el custom
  // element) mientras nadie active esa vista, así no duplica el recuento de nodos del
  // DOM de cada card en el 100% de las sesiones que nunca la usan. Estos dos helpers
  // deciden cuándo clonar ese contenido y convertirlo en una <card-gallery> real.
  // Global (no hay sistema de módulos en este tema; mismo patrón que window.analyticsUtils,
  // window.sizeChartHelpers, etc.) porque infinite-scroll.js y collection-grid-layout.js
  // también necesitan llamarlo.
  window.altGalleryHelpers = {
    isActive() {
      const grid = document.getElementById('product-grid');
      return !!grid && (grid.classList.contains('vista-4') ||
        (grid.classList.contains('is-mobile') && grid.classList.contains('vista-2')));
    },
    instantiateIn(root) {
      if (!this.isActive()) return;
      root.querySelectorAll('template.alt-gallery-template').forEach((tpl) => {
        const node = tpl.content.firstElementChild;
        if (node) tpl.replaceWith(node.cloneNode(true));
      });
    },

    // Precarga los BYTES (no el DOM) de la 1ª foto de la galería alternativa de las
    // cards ya visibles o a menos de 1 pantalla, sin instanciar su <template> — arranca
    // la descarga en cuanto el usuario empieza a interactuar con el selector de vista
    // (pointerdown/pointerover, antes del click real que hace instantiateIn()), para que
    // la foto esté lista o casi lista cuando de verdad aparece. Acotado a cards cercanas
    // a propósito: si se hiciera para toda la parrilla volveríamos a cargar la galería
    // duplicada de golpe para el 100% de las sesiones, justo lo que se quitó.
    prefetchVisibleFirstPhotos() {
      if (this.isActive()) return; // ya activa: el toggle va a OCULTARLA, no a mostrarla
      const margin = window.innerHeight;
      // Saltar los slides de la otra plataforma: la 1ª foto que se verá es la de esta.
      const hiddenSlide = window.matchMedia('(max-width: 767.98px)').matches
        ? '.card-gallery__slide--desktop-only'
        : '.card-gallery__slide--mobile-only';
      const firstImgSelector = `.card-gallery__slide:not(${hiddenSlide}) img.card__img[data-src]`;
      document.querySelectorAll('.collection__grid__item').forEach((card) => {
        const rect = card.getBoundingClientRect();
        if (rect.bottom < -margin || rect.top > margin * 2) return;
        const img = card
          .querySelector('template.alt-gallery-template')
          ?.content.querySelector(firstImgSelector);
        if (img?.dataset.src) new Image().src = img.dataset.src;
      });
    },
  };

  // pointerdown cubre touch y ratón en el momento del primer contacto; pointerover suma
  // algo más de margen en desktop (el usuario suele pasar el cursor antes de hacer clic).
  // Delegado en document (no en los botones directamente) porque el script inline de
  // main-collection-product-grid.liquid clona #gridToggleBtn para limpiar listeners
  // antiguos cada vez que se reconstruye el toolbar — un listener puesto directamente en
  // el botón se perdería en el primer reclonado.
  // requestIdleCallback (no llamada directa): prefetchVisibleFirstPhotos() recorre TODAS
  // las cards de la parrilla y les pide getBoundingClientRect(), un reflow síncrono que en
  // una parrilla larga (scroll infinito acumulado) puede tardar y competir justo con el
  // click real del botón, que tiene que llegar sí o sí. Diferirlo aquí garantiza que este
  // listener nunca bloquea el camino crítico del toggle, a costa de arrancar la precarga
  // unos milisegundos más tarde — irrelevante para su propósito.
  ['pointerdown', 'pointerover'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      if (!event.target.closest('#gridToggleBtn, .collection__mobile-layout-switch')) return;
      const runPrefetch = () => window.altGalleryHelpers.prefetchVisibleFirstPhotos();
      if ('requestIdleCallback' in window) {
        requestIdleCallback(runPrefetch, { timeout: 200 });
      } else {
        setTimeout(runPrefetch, 0);
      }
    });
  });

  // La preferencia de vista guardada (localStorage) la aplica initGridToggle() en
  // sections/main-collection-product-grid.liquid, registrada como listener de
  // DOMContentLoaded (no se ejecuta de forma síncrona) — ese listener se registró antes
  // (durante el parseo del script inline, que precede a cualquier script defer como este
  // fichero), así que el nuestro, registrado aquí, se dispara después y ve la clase de
  // vista ya aplicada. Cubre al usuario recurrente que ya carga con la vista alternativa
  // activa desde el principio, sin esperar a un toggle que no va a llegar.
  document.addEventListener('DOMContentLoaded', () => {
    window.altGalleryHelpers.instantiateIn(document);
  });

  // Escucha única a nivel de módulo (no una por instancia, para no acumular cientos de
  // listeners al ir revelando cards con el scroll infinito). collection:viewToggled lo
  // dispara collection-grid-layout.js al detectar un cambio de clase vista-*/is-mobile/
  // is-desktop en la parrilla, sin importar cuál de los 3 sitios que la cambian lo disparó.
  document.addEventListener('collection:viewToggled', () => {
    // Instanciar primero: las <card-gallery> recién clonadas se auto-hidratan solas vía
    // su propio connectedCallback(), así que el barrido de abajo queda como red de
    // seguridad para lo que ya estaba instanciado de antes (no para lo recién creado).
    window.altGalleryHelpers.instantiateIn(document);

    // Filtro barato primero (no toca layout): con scroll infinito acumulado puede haber
    // cientos de <card-gallery> en la página, y leer offsetParent de cada una fuerza un
    // reflow síncrono por elemento — un solo toggle podía bloquear el hilo principal
    // varios cientos de ms justo cuando se están disparando las descargas de imagen.
    // Solo las copias aún no hidratadas conservan data-src en su 1er slide, así que
    // reducimos el barrido a esas antes de comprobar visibilidad.
    // Incluye la slide que sigue a una de solo-mobile/solo-desktop: puede ser la 1ª visible.
    document.querySelectorAll(
      '.card-gallery__slide:first-child img.card__img[data-src], ' +
      '.card-gallery__slide--mobile-only + .card-gallery__slide img.card__img[data-src], ' +
      '.card-gallery__slide--desktop-only + .card-gallery__slide img.card__img[data-src]',
    ).forEach((img) => {
      const gallery = img.closest('card-gallery');
      if (gallery && gallery._hydrateFirstSlideIfVisible) gallery._hydrateFirstSlideIfVisible();
    });
  });
}
